// Los nombres legibles de lo que una regla menciona: métricas, períodos y el título de una regla de
// sistema. No es React, pero sí depende del catálogo del Tablero (`columns.js`, `indicators.js`) y de
// `t`: por eso vive aparte del modelo puro.
import {getPeriodsData, getPeriodsToText, resolveMetricDisplayName} from '../dashboard/columns';
import {getLabelForMetricWithCustom} from '../dashboard/metric_groups';
import {getIndicatorData} from '../../indicators';
import {
    indicatorOf, isFlexIndicator, isSingleThresholdIndicator, thresholdLabelKey, UNIT_DAYS, visibleThresholdLevels,
} from './systemRulesModel';
import {
    CONDITION_PERIOD_CHANGE,
    draftFromRule,
    formatThreshold,
    hiddenScopeCount,
    isLineMetric,
    isRuleOn,
    isSystemRule,
    namesText,
    operatorKey,
    PAUSED_CREATOR_LEFT,
    PAUSED_LIMIT,
    parseValue,
    SCOPE_AGENCY,
    SCOPE_ANY,
    SCOPE_CLIENT,
    scopeNamesFromRule,
    sentenceTokens,
    systemFamily,
    UNIT_PERCENTAGE,
    unitOfMetric,
} from './rulesModel';

export function periodLabel(period, t) {
    return getPeriodsData(t)[period] || period || '';
}

// Las métricas de la línea (`LINE:*`) son fijas del producto: se nombran en el idioma de quien mira. El nombre
// que manda el backend (en español) es la red para una nueva (review C-av2-fe-centro-16).
// Las dos de un objetivo puntual no se ofrecen en el constructor (son sólo de `GOALS_1` / `GOALS_2`, D9), pero
// se nombran igual.
const LINE_METRIC_KEYS = ['BUDGET_SPENT_PCT', 'PACING_DEVIATION_PCT', 'GOAL_DEVIATION_PCT',
    'GOAL_1_DEVIATION_PCT', 'GOAL_2_DEVIATION_PCT',
    'IDEAL_DAILY_SPEND_DEVIATION_YESTERDAY_PCT', 'IDEAL_DAILY_SPEND_DEVIATION_LAST_7_DAYS_PCT',
    'IDEAL_DAILY_SPEND_DEVIATION_TODAY_PCT'];

/** El nombre traducido de una `LINE:*` conocida, o `null`. La clave no lleva el «:» (separa namespaces). */
export function lineMetricLabel(metric, t) {
    if (!t || !isLineMetric(metric)) return null;
    const key = String(metric).slice('LINE:'.length);
    return LINE_METRIC_KEYS.includes(key) ? t(`alerts_center.line_metric.${key}`) : null;
}

/**
 * El nombre de una métrica: las personalizadas por su nombre (de `options` o del `metric_label` que
 * manda el backend), las del catálogo por el suyo. Nunca «CUSTOM:3» en pantalla. Con `t`, las de la línea
 * en el idioma de quien mira.
 */
export function metricLabel(metric, {options = null, fallbackLabel = null, customMetrics = [], t = null} = {}) {
    if (!metric) return '';
    const line = lineMetricLabel(metric, t);
    if (line) return line;
    const fromOptions = options && Array.isArray(options.metrics)
        ? options.metrics.find((item) => item.id === metric) : null;
    if (fromOptions && fromOptions.label) return fromOptions.label;
    if (fallbackLabel) return fallbackLabel;
    const label = getLabelForMetricWithCustom(metric, customMetrics);
    return label && label !== metric ? label : resolveMetricDisplayName(metric);
}

/**
 * `{title, subtitle}` de una regla de sistema, con los nombres de siempre (`getIndicatorData`). Las
 * métricas comparativas toman el nombre y los períodos que manda el catálogo (`flex`): el id de una
 * comparativa de ecommerce no trae períodos y `getIndicatorData` armaría «undefined vs undefined».
 */
export function systemRuleTitle(rule, t) {
    const indicator = indicatorOf(rule);
    if (isFlexIndicator(indicator)) {
        const flex = rule.flex || {};
        const parts = String(indicator).split('||');
        const title = flex.metric_label || resolveMetricDisplayName(flex.metric || parts[1] || '') || indicator;
        const main = flex.main_period || parts[2];
        const vs = flex.vs_period || parts[3];
        let subtitle = '';
        if (main && vs) subtitle = getPeriodsToText(t, main, vs);
        else if (main) subtitle = periodLabel(main, t);
        return {title, subtitle: subtitle || ''};
    }
    const data = indicator ? getIndicatorData(indicator, t) : null;
    if (!data) return {title: indicator || String(rule.id || ''), subtitle: ''};
    return {title: data.title || indicator, subtitle: data.subtitle || ''};
}

export function systemRuleSearchText(rule, t) {
    const {title, subtitle} = systemRuleTitle(rule, t);
    return `${title} ${subtitle}`;
}

function formatThresholdValue(value, unit, t, locale) {
    if (value === null || value === undefined || value === '') return t('alerts_center.rules.threshold_none');
    if (unit === UNIT_DAYS) return t('alerts_center.rules.days', {count: Number(value)});
    return formatThreshold(value, UNIT_PERCENTAGE, locale);
}

/**
 * «Malo: 15 % · Crítico: 25 %» (o un solo número en Hoy), con los niveles que el indicador tiene: un «Malo»
 * que el backend todavía mande en un indicador de un solo umbral no se muestra. Los de un solo umbral dicen
 * desde dónde avisan: «Avisa desde el 20 %».
 */
export function thresholdsSummary(rule, t, locale) {
    const thresholds = rule.thresholds || [];
    const ordered = visibleThresholdLevels(rule)
        .map((level) => thresholds.find((item) => item.level === level))
        .filter(Boolean);
    if (isSingleThresholdIndicator(indicatorOf(rule))) {
        return ordered.map((item) => t('alerts_center.rules.threshold_summary_single', {
            value: formatThresholdValue(item.value, item.unit, t, locale),
            interpolation: {escapeValue: false},
        })).join(' · ');
    }
    return ordered.map((item) => t('alerts_center.rules.threshold_summary', {
        level: t(thresholdLabelKey(rule, item.level)),
        value: formatThresholdValue(item.value, item.unit, t, locale),
        interpolation: {escapeValue: false},
    })).join(' · ');
}

/** La frase de una regla personalizada guardada, desde su estructura y los nombres resueltos. */
export function customRuleTokens(rule, draft, {t, locale, options = null}) {
    const condition = rule.condition || {};
    const recipients = (rule.delivery && rule.delivery.recipients) || {};
    return sentenceTokens(draft, {
        t,
        locale,
        unit: condition.unit || unitOfMetric(condition.metric, options),
        metricLabel: (metric) => metricLabel(metric, {options, fallbackLabel: condition.metric_label, t}),
        periodLabel: (period) => periodLabel(period, t),
        scopeNames: scopeNamesFromRule(rule),
        hiddenCount: hiddenScopeCount(rule),
        recipientNames: (recipients.resolved || []).map((user) => user.name).filter(Boolean),
    });
}

export function customRuleSearchText(rule) {
    const condition = rule.condition || {};
    return `${rule.name || ''} ${condition.metric_label || ''} ${condition.metric || ''}`;
}

// ----------------------------------------------------------- v2: la fila de «Mis alertas» ---------
// Las de Dashbo se nombran por su `system_key` (contrato v2 §9: `alerts_center.system.<clave>.*`), así se
// leen en el idioma de quien mira; el `name` que manda el backend es la red para una clave nueva. Los objetivos
// son dos filas, una por objetivo y cada una con su número (D9, fede 18/10): `GOALS_1` y `GOALS_2`.

export const SYSTEM_KEYS_WITH_TEXT = [
    'BUDGET_1', 'BUDGET_2', 'GOALS_1', 'GOALS_2', 'PACING', 'ODS_YESTERDAY', 'ODS_7D', 'ODS_TODAY',
    'ROAS_VS_LAST_MONTH', 'ACOS_VS_LAST_MONTH',
    'URL_DOWN', 'NO_IMPRESSIONS', 'DAILY_LIMIT', 'UNCONTROLLED_SPEND', 'FUNDS', 'UNCOVERED_SPEND', 'ODS_VS_PLATFORM',
];

// Las de umbral cuya frase tiene un texto propio alrededor del número (el resto se arma de su condición).
const SYSTEM_KEYS_WITH_SENTENCE = ['BUDGET_1', 'BUDGET_2', 'GOALS_1', 'GOALS_2', 'PACING', 'ODS_YESTERDAY', 'ODS_7D',
    'ODS_TODAY'];

export function isFlexKey(systemKey) {
    return String(systemKey || '').startsWith('FLEX:');
}

// Por familia: una hermana para unas líneas (`BUDGET_1@90`) se nombra como su alerta (review C-av2-fe-centro-02).
function knownKey(systemKey) {
    const family = systemFamily(systemKey);
    return SYSTEM_KEYS_WITH_TEXT.includes(family) ? family : null;
}

// Los nombres de una comparativa: los de la condición de la fila, o los de `flex` de una sugerida.
function flexNames(item, t) {
    const condition = item.condition || {};
    const flex = item.flex || {};
    return {
        metric: flex.metric_label || condition.metric_label || metricLabel(flex.metric || condition.metric, {t}),
        period: lowerFirstOf(periodLabel(flex.main_period || condition.period, t)),
        vs: lowerFirstOf(periodLabel(flex.vs_period || condition.vs_period, t)),
    };
}

function lowerFirstOf(text) {
    return text ? text.charAt(0).toLowerCase() + text.slice(1) : '';
}

/** El título de una fila (o de una sugerida): el nombre de la propia, el de Dashbo por su clave. */
export function alertTitle(item, t) {
    if (!item) return '';
    const systemKey = item.system_key;
    if (!isSystemRule(item) && !systemKey) return item.name || '';
    if (isFlexKey(systemKey)) {
        const names = flexNames(item, t);
        return t('alerts_center.system.flex.title', {...names, interpolation: {escapeValue: false}});
    }
    const key = knownKey(systemKey);
    return key ? t(`alerts_center.system.${key}.title`) : (item.name || systemKey || '');
}

// En qué se fija cada aviso de control (ronda 5): sus managers mandan sin mirar el alcance, así que se dice fijo.
const CONTROL_SCOPE_KEYS = {
    URL_DOWN: 'alerts_center.row.control_scope_lines',
    NO_IMPRESSIONS: 'alerts_center.row.control_scope_lines',
    UNCOVERED_SPEND: 'alerts_center.row.control_scope_lines',
    ODS_VS_PLATFORM: 'alerts_center.row.control_scope_lines',
    DAILY_LIMIT: 'alerts_center.row.control_scope_accounts',
    FUNDS: 'alerts_center.row.control_scope_accounts',
    UNCONTROLLED_SPEND: 'alerts_center.row.control_scope_agency',
};

/** «En todas las líneas», «En todas las cuentas», «En toda la agencia»: dónde mira un aviso de control. */
export function controlScopeText(item, t) {
    const key = CONTROL_SCOPE_KEYS[systemFamily(item && item.system_key)];
    return t(key || 'alerts_center.row.control_scope_agency');
}

/** Lo que explica un aviso de control debajo de su título. */
export function controlDescription(item, t) {
    const key = knownKey(item && item.system_key);
    return key ? t(`alerts_center.system.${key}.description`) : '';
}

function operatorWord(operator, t) {
    return t(operatorKey(operator));
}

/**
 * La frase de la fila partida en lo que va antes y después del número tocable: `{pre, post, unit}`.
 * - Una de Dashbo de umbral: su texto propio («Cuando lo invertido en el período supera el » [105 %] « del
 *   presupuesto de la línea.»).
 * - Una de Dashbo que compara contra otro período (ROAS, ACOS, las comparativas): «Cuando ROAS (este mes)
 *   cae más de un » [25 %] « contra el mes pasado.».
 * - Una propia: la frase del constructor desde «cuando», sin el canal ni el destinatario (que ya están en la
 *   fila): «Cuando el cliente Dashbo Ads tenga CPA por encima de » [$3.000] « en este período.».
 */
export function alertSentence(item, {t, locale = 'es'}) {
    const condition = (item && item.condition) || {};
    const key = knownKey(item && item.system_key);
    if (key && SYSTEM_KEYS_WITH_SENTENCE.includes(key)) {
        return {
            pre: t(`alerts_center.system.${key}.sentence_pre`),
            post: t(`alerts_center.system.${key}.sentence_post`),
            unit: UNIT_PERCENTAGE,
        };
    }
    if (condition.type === CONDITION_PERIOD_CHANGE && (isSystemRule(item) || item.system_key)) {
        const names = isFlexKey(item.system_key) ? flexNames(item, t) : {
            metric: metricLabel(condition.metric, {fallbackLabel: condition.metric_label, t}),
            period: lowerFirstOf(periodLabel(condition.period, t)),
            vs: lowerFirstOf(periodLabel(condition.vs_period, t)),
        };
        // Sin los períodos (una sugerida a medias), la frase no deja un «()» vacío.
        return {
            pre: t(names.period ? 'alerts_center.row.change_pre' : 'alerts_center.row.change_pre_plain', {
                metric: names.metric, period: names.period, operator: operatorWord(condition.operator, t),
                interpolation: {escapeValue: false},
            }),
            post: names.vs
                ? t('alerts_center.row.change_post', {vs: names.vs, interpolation: {escapeValue: false}})
                : t('alerts_center.row.change_post_plain'),
            unit: UNIT_PERCENTAGE,
        };
    }
    const tokens = customRuleTokens(item, draftFromRule(item, {locale}), {t, locale});
    const from = tokens.findIndex((token) => token.type === 'word' && token.text === t('alerts_center.builder.sentence_when'));
    const rest = from === -1 ? tokens : tokens.slice(from);
    const valueAt = rest.findIndex((token) => token.slot === 'value');
    const text = (list) => list.map((token) => token.text).join(' ');
    const pre = text(rest.slice(0, valueAt));
    return {
        pre: `${pre.charAt(0).toUpperCase()}${pre.slice(1)} `,
        post: ` ${text(rest.slice(valueAt + 1))}.`,
        unit: condition.type === CONDITION_PERIOD_CHANGE ? UNIT_PERCENTAGE : (condition.unit || unitOfMetric(condition.metric)),
    };
}

/**
 * Lo que rodea al número en su editor: «Avisar cuando lo invertido supera el» [−] [105] [+] «% del
 * presupuesto». En una comparación, el operador va como chip entre las dos partes (`withOperator`).
 */
export function editorText(item, {t}) {
    const condition = (item && item.condition) || {};
    const key = knownKey(item && item.system_key);
    if (key && SYSTEM_KEYS_WITH_SENTENCE.includes(key)) {
        return {pre: t(`alerts_center.system.${key}.editor_pre`), post: t(`alerts_center.system.${key}.editor_post`), withOperator: false};
    }
    if (condition.type === CONDITION_PERIOD_CHANGE) {
        const names = isFlexKey(item.system_key) ? flexNames(item, t) : {
            metric: metricLabel(condition.metric, {fallbackLabel: condition.metric_label, t}),
            period: lowerFirstOf(periodLabel(condition.period, t)),
            vs: lowerFirstOf(periodLabel(condition.vs_period, t)),
        };
        return {
            pre: t(names.period ? 'alerts_center.editor.change_pre' : 'alerts_center.editor.change_pre_plain',
                {metric: names.metric, period: names.period, interpolation: {escapeValue: false}}),
            post: names.vs
                ? t('alerts_center.editor.change_post', {vs: names.vs, interpolation: {escapeValue: false}})
                : t('alerts_center.editor.change_post_plain'),
            withOperator: true,
        };
    }
    return {
        pre: t('alerts_center.editor.custom_pre', {
            metric: metricLabel(condition.metric, {fallbackLabel: condition.metric_label, t}), operator: t(operatorKey(condition.operator)),
            interpolation: {escapeValue: false},
        }),
        post: t('alerts_center.editor.custom_post', {
            period: lowerFirstOf(periodLabel(condition.period, t)), interpolation: {escapeValue: false},
        }),
        withOperator: false,
    };
}

/** La frase de una sugerida, con el número que la agencia usa hoy («… supera el 115 % del presupuesto…»). */
export function suggestionSentence(suggestion, {t, locale = 'es'}) {
    const parts = alertSentence({...suggestion, kind: 'SYSTEM'}, {t, locale});
    const value = formatThreshold(suggestion.condition && suggestion.condition.value, parts.unit, locale);
    return `${parts.pre}${value}${parts.post}`;
}

// ----------------------------------------------------------- UD-18: excepciones y advertencias ------

// Las líneas de una excepción con su cliente: las que se miden (`resolved`) y, si el backend las nombra, las que hoy no
// (`scope.inactive`, P-2 de la tanda). Sin esa clave, una línea de un cliente inactivo no tiene nombre.
function exceptionLines(rule) {
    const scope = (rule && rule.scope) || {};
    return [...(Array.isArray(scope.resolved) ? scope.resolved : []), ...(Array.isArray(scope.inactive) ? scope.inactive : [])];
}

/** Cuántas líneas tiene una excepción: las que ve quien mira (con las que hoy no se miden) y las que no ve. */
export function exceptionLineCount(rule) {
    const scope = (rule && rule.scope) || {};
    const ids = Array.isArray(scope.ids) ? scope.ids.length : 0;
    return Math.max(ids, exceptionLines(rule).length) + hiddenScopeCount(rule);
}

/**
 * El título de una excepción (UD-18): «Excepción en la línea «Todo Facebook» · Gaby Corp», «Excepción en 3 líneas:
 * «Search» · Dorfman, …», o sin nombres «Excepción en 1 línea».
 */
export function exceptionTitle(rule, t) {
    const lines = exceptionLines(rule);
    const count = Math.max(1, exceptionLineCount(rule));
    if (count === 1 && lines.length === 1) {
        const line = lines[0];
        return line.client_name
            ? t('alerts_center.mr.exception_line', {line: line.name, client: line.client_name, interpolation: {escapeValue: false}})
            : t('alerts_center.mr.exception_line_plain', {line: line.name, interpolation: {escapeValue: false}});
    }
    if (lines.length) {
        const names = lines.map((line) => (line.client_name
            ? t('alerts_center.mr.exception_line_name', {line: line.name, client: line.client_name, interpolation: {escapeValue: false}})
            : `«${line.name}»`));
        return t('alerts_center.mr.exception_lines', {count, names: namesText(names, t, {more: count - lines.length}),
            interpolation: {escapeValue: false}});
    }
    return t('alerts_center.mr.exception_count', {count});
}

/**
 * La frase de una excepción, contra la de su alerta: `{pre, value, post}` (`value` es el número que va resaltado, o
 * `null`). «Ahí avisa con el mismo 25 %.», «Ahí sigue prendida, con el mismo 25 %.» (su alerta está pausada), «Ahí
 * avisa con su propio número: cuando el gasto de hoy pasa en más de un 100 % …» o, pausada, «En esa línea no avisa.».
 */
export function exceptionSentence(rule, parent, {t, locale = 'es'}) {
    const count = Math.max(1, exceptionLineCount(rule));
    if (!isRuleOn(rule)) return {pre: t('alerts_center.mr.exception_off', {count}), value: null, post: ''};
    const parts = alertSentence(rule, {t, locale});
    const value = formatThreshold(rule.condition && rule.condition.value, parts.unit, locale);
    const parentValue = parent && parent.condition ? parseValue(parent.condition.value, locale) : null;
    const own = rule.condition ? parseValue(rule.condition.value, locale) : null;
    if (parent && own !== null && own === parentValue
        && (rule.condition.operator || null) === ((parent.condition && parent.condition.operator) || null)) {
        const key = isRuleOn(parent) ? 'alerts_center.mr.exception_same' : 'alerts_center.mr.exception_same_parent_off';
        return {pre: t(`${key}_pre`), value, post: t(`${key}_post`)};
    }
    return {pre: `${t('alerts_center.mr.exception_own')}${lowerFirstOf(parts.pre)}`, value, post: parts.post};
}

/**
 * La advertencia de una fila, corta y en ámbar (UD-18): por qué no avisa aunque se vea activa, o por qué la pausaron
 * sin que nadie tocara el switch. `null` si no hay nada que advertir.
 */
export function ruleWarning(rule, t) {
    if (!rule) return null;
    if (rule.paused_reason === PAUSED_CREATOR_LEFT) return t('alerts_center.mr.warn_creator_left');
    if (rule.paused_reason === PAUSED_LIMIT) return t('alerts_center.mr.warn_limit');
    if (!isRuleOn(rule)) return null;
    const scope = rule.scope || {};
    if (scope.type === SCOPE_AGENCY || scope.type === SCOPE_ANY) return null;
    const stats = rule.stats || {};
    const inactiveCount = typeof scope.inactive_count === 'number' ? scope.inactive_count : 0;
    // Avisa en algo que hoy no se mide (una línea en pausa o de un cliente inactivo), y en nada más.
    if (stats.lines_in_scope !== 0 || inactiveCount < 1) return null;
    const inactive = Array.isArray(scope.inactive) ? scope.inactive : [];
    const clients = [...new Set(inactive
        .filter((item) => scope.type === SCOPE_CLIENT || item.reason === 'CLIENT_INACTIVE')
        .map((item) => (scope.type === SCOPE_CLIENT ? item.name : item.client_name))
        .filter(Boolean))];
    if (clients.length === 1 && inactive.length === inactiveCount) {
        return t('alerts_center.mr.warn_client_inactive', {client: clients[0], interpolation: {escapeValue: false}});
    }
    return t('alerts_center.mr.warn_not_measured', {count: inactiveCount});
}
